import { useState } from 'react'

import { CreateSourceInput, FindByIdInput, Source, SourceByApiKeyInput, SourceType } from '@ui/__generated__/graphql'
import { needsDatableApiKey } from '@ui/lib/utils/sources'
import { CreateSourceQuery, GetSourceByApiKeyQuery, GetSourceWithLastSeenLazyQuery } from '@ui/loaders/sources'
import { FeatureFlagName, useIsFeatureEnabled } from '@ui/providers/FeatureFlagProvider'

export type SelectableSourceType = Exclude<SourceType, SourceType.CatchAll>

export enum OnboardingStep {
  SelectSourceType,
  ConfigureSource,
  DetectingSource, // for those sources we can detect, move to ConfigurationSuccessful / ConfigurationUnsuccessful
  ConfigurationSuccessful,
  ConfigurationUnsuccessful,
  StartTransforming, // for those sources we can't detect, just start transforming
}

export type DetectSource = (onSuccess: (source: Source) => void, onFailure: () => void) => Promise<void>

type UseOnboarding = ({
  selectedSourceType,
  setStep,
}: {
  selectedSourceType: SourceType | null
  setStep: (step: OnboardingStep) => void
}) => {
  onDataSourceConfigured: (apiKey: string) => Promise<void>
  detectSource: DetectSource
}

const isDetected = (source: Source) => {
  return source.lastSeen !== null
}

const useOldOnboarding: UseOnboarding = ({ selectedSourceType, setStep }) => {
  const [apiKey, setApiKey] = useState<string>('')
  const [getSourceByApiKey] = GetSourceByApiKeyQuery()

  return {
    onDataSourceConfigured: async (apiKey: string) => {
      setApiKey(apiKey)

      if (needsDatableApiKey(selectedSourceType as SourceType)) {
        // no datable API key available in the old onboarding flow,
        // so proceed to start transforming
        setStep(OnboardingStep.StartTransforming)
      } else {
        setStep(OnboardingStep.DetectingSource)
      }
    },
    detectSource: async (onSuccess, onFailure) => {
      const input: SourceByApiKeyInput = { apiKey }
      const { data } = await getSourceByApiKey({ variables: { input } })

      if (data?.sourceByApiKey) {
        onSuccess(data.sourceByApiKey)
      } else {
        onFailure()
      }
    },
  }
}

const useNewOnboarding: UseOnboarding = ({ selectedSourceType, setStep }) => {
  const [createSource, { data }] = CreateSourceQuery()
  const [getSource] = GetSourceWithLastSeenLazyQuery()

  return {
    onDataSourceConfigured: async (apiKey: string) => {
      if (!selectedSourceType) return

      const input: CreateSourceInput = {
        apiKey,
        type: selectedSourceType,
      }

      await createSource({ variables: { input } })

      setStep(OnboardingStep.DetectingSource)
    },
    detectSource: async (onSuccess, onFailure) => {
      if (!data?.createSource) return

      const input: FindByIdInput = {
        id: data.createSource?.id.toString(),
      }

      const { data: getSourceData } = await getSource({ variables: { input } })

      if (getSourceData?.source && isDetected(getSourceData.source)) {
        onSuccess(getSourceData.source)
      } else {
        onFailure()
      }
    },
  }
}

export const useOnboarding = (): ReturnType<UseOnboarding> & {
  step: OnboardingStep
  setStep: (step: OnboardingStep) => void
  selectedSourceType: SelectableSourceType | null
  setSelectedSourceType: (source: SelectableSourceType | null) => void
} => {
  const createSourceAndLastSeenEnabled = useIsFeatureEnabled(FeatureFlagName.CreateSourceAndLastSeen)

  const [step, setStep] = useState(OnboardingStep.SelectSourceType)
  const [selectedSourceType, setSelectedSourceType] = useState<SelectableSourceType | null>(null)

  const newOnboarding = useNewOnboarding({ selectedSourceType, setStep })
  const oldOnboarding = useOldOnboarding({ selectedSourceType, setStep })

  return {
    step,
    setStep,
    setSelectedSourceType,
    selectedSourceType,
    ...(createSourceAndLastSeenEnabled ? newOnboarding : oldOnboarding),
  }
}
